import React, { useState, useEffect } from 'react';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Plus, Target, Clock, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const GoalsRoutines = () => {
  const [children, setChildren] = useState([]);
  const [goals, setGoals] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [goalDialogOpen, setGoalDialogOpen] = useState(false);
  const [routineDialogOpen, setRoutineDialogOpen] = useState(false);
  const [newGoal, setNewGoal] = useState({
    title: '',
    description: '',
    target_type: 'child',
    child_id: ''
  });
  const [newRoutine, setNewRoutine] = useState({
    title: '',
    child_id: '',
    schedule: [{ time: '07:00', activity: '' }]
  });

  useEffect(() => {
    loadChildren();
    loadGoals();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      loadRoutines();
    }
  }, [selectedChild]);

  const loadChildren = async () => {
    try {
      const response = await API.get('/children');
      setChildren(response.data);
      if (response.data.length > 0) {
        setSelectedChild(response.data[0].child_id);
      }
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadGoals = async () => {
    try {
      const response = await API.get('/goals');
      setGoals(response.data);
    } catch (error) {
      console.error('Failed to load goals:', error);
    }
  };

  const loadRoutines = async () => {
    try {
      const response = await API.get(`/routines?child_id=${selectedChild}`);
      setRoutines(response.data);
    } catch (error) {
      console.error('Failed to load routines:', error);
    }
  };

  const handleCreateGoal = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await API.post('/goals', newGoal);
      toast.success('Goal created!');
      setGoalDialogOpen(false);
      setNewGoal({ title: '', description: '', target_type: 'child', child_id: '' });
      await loadGoals();
    } catch (error) {
      toast.error('Failed to create goal');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateRoutine = async (e) => {
    e.preventDefault();
    if (submitting) return;
    
    setSubmitting(true);
    try {
      await API.post('/routines', newRoutine);
      toast.success('Routine created!');
      setRoutineDialogOpen(false);
      setNewRoutine({ title: '', child_id: selectedChild, schedule: [{ time: '07:00', activity: '' }] });
      await loadRoutines();
    } catch (error) {
      toast.error('Failed to create routine');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateProgress = async (goalId, progress) => {
    try {
      await API.put(`/goals/${goalId}/progress?progress=${progress}`);
      loadGoals();
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const handleDeleteGoal = async (goalId) => {
    try {
      await API.delete(`/goals/${goalId}`);
      toast.success('Goal deleted');
      loadGoals();
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const addScheduleItem = () => {
    setNewRoutine(prev => ({
      ...prev,
      schedule: [...prev.schedule, { time: '', activity: '' }]
    }));
  };

  const updateScheduleItem = (index, field, value) => {
    setNewRoutine(prev => ({
      ...prev,
      schedule: prev.schedule.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const removeScheduleItem = (index) => {
    setNewRoutine(prev => ({
      ...prev,
      schedule: prev.schedule.filter((_, i) => i !== index)
    }));
  };

  const handleAISuggestRoutine = async () => {
    if (!selectedChild) {
      toast.error('Please select a child first');
      return;
    }
    try {
      const response = await API.post(`/ai/suggest-routine?child_id=${selectedChild}`);
      setNewRoutine(prev => ({
        ...prev,
        schedule: response.data.routine
      }));
      toast.success('AI suggestion loaded!');
    } catch (error) {
      toast.error('Failed to get AI suggestion');
    }
  };

  return (
    <div className="space-y-8 max-w-6xl">
      <div>
        <h1 className="text-4xl font-bold mb-2">Goals & Routines</h1>
        <p className="text-muted-foreground">Track progress and build structure</p>
      </div>

      <Tabs defaultValue="goals">
        <TabsList>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="routines">Routines</TabsTrigger>
        </TabsList>

        <TabsContent value="goals" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Your Goals</h2>
            <Dialog open={goalDialogOpen} onOpenChange={setGoalDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Goal
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Goal</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateGoal} className="space-y-4">
                  <div>
                    <Label htmlFor="goal-title">Title</Label>
                    <Input
                      id="goal-title"
                      value={newGoal.title}
                      onChange={(e) => setNewGoal({ ...newGoal, title: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="goal-description">Description</Label>
                    <Textarea
                      id="goal-description"
                      value={newGoal.description}
                      onChange={(e) => setNewGoal({ ...newGoal, description: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Target Type</Label>
                    <Select
                      value={newGoal.target_type}
                      onValueChange={(value) => setNewGoal({ ...newGoal, target_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="child">Child</SelectItem>
                        <SelectItem value="parent">Parent (Self)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newGoal.target_type === 'child' && (
                    <div>
                      <Label>Child</Label>
                      <Select
                        value={newGoal.child_id}
                        onValueChange={(value) => setNewGoal({ ...newGoal, child_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {children.map(child => (
                            <SelectItem key={child.child_id} value={child.child_id}>
                              {child.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? 'Creating...' : 'Create Goal'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {goals.map(goal => (
              <Card key={goal.goal_id}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-primary" />
                        {goal.title}
                      </CardTitle>
                      <CardDescription className="mt-2">{goal.description}</CardDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteGoal(goal.goal_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Progress</span>
                      <span className="text-sm font-bold">{goal.progress}%</span>
                    </div>
                    <Progress value={goal.progress} />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateProgress(goal.goal_id, Math.min(goal.progress + 10, 100))}
                    >
                      +10%
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateProgress(goal.goal_id, Math.max(goal.progress - 10, 0))}
                    >
                      -10%
                    </Button>
                  </div>
                  {goal.completed && (
                    <Badge variant="default" className="bg-green-600">Completed!</Badge>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {goals.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">No goals yet. Create your first goal!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="routines" className="space-y-6">
          {children.length > 0 && (
            <div className="flex items-center justify-between">
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {children.map(child => (
                    <SelectItem key={child.child_id} value={child.child_id}>
                      {child.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Dialog open={routineDialogOpen} onOpenChange={setRoutineDialogOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    New Routine
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create Daily Routine</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateRoutine} className="space-y-4">
                    <div>
                      <Label htmlFor="routine-title">Routine Name</Label>
                      <Input
                        id="routine-title"
                        value={newRoutine.title}
                        onChange={(e) => setNewRoutine({ ...newRoutine, title: e.target.value })}
                        placeholder="e.g., Morning Routine"
                        required
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <Label>Schedule</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAISuggestRoutine}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        AI Suggest
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {newRoutine.schedule.map((item, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            type="time"
                            value={item.time}
                            onChange={(e) => updateScheduleItem(index, 'time', e.target.value)}
                            required
                          />
                          <Input
                            placeholder="Activity"
                            value={item.activity}
                            onChange={(e) => updateScheduleItem(index, 'activity', e.target.value)}
                            required
                          />
                          {newRoutine.schedule.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeScheduleItem(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    <Button type="button" variant="outline" onClick={addScheduleItem}>
                      Add Time Slot
                    </Button>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? 'Creating...' : 'Create Routine'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className="space-y-4">
            {routines.map(routine => (
              <Card key={routine.routine_id}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    {routine.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {routine.schedule.map((item, index) => (
                      <div key={index} className="flex items-center gap-4 p-2 rounded-lg bg-gray-50">
                        <span className="font-mono font-semibold text-primary">{item.time}</span>
                        <span>{item.activity}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {routines.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">No routines yet. Create your first routine!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GoalsRoutines;
